/**
 * Variant 2-frame stability gate — b13c.7 / v1.4.7 build 1.
 *
 * A variant is only allowed to commit writes once it has been the SAME
 * classifier answer across the most recent 2 qualifying frames, where a
 * qualifying frame is `mode == "grid_4x4"` AND classifier confidence ≥ 80.
 *
 * Why: the build-196 hypothesis is that the variant classifier occasionally
 * flashes Shadow/Mega for a single frame during a panel swipe (the variant
 * pill is mid-transition). Those single-frame transients are exactly the
 * phantom Shadow/Mega writes that polluted the dex in pre-b13c.6 builds. A
 * genuine variant change holds for at least two consecutive confident frames;
 * a transient does not. So we require two-in-a-row before the variant's cells
 * may be committed.
 *
 * This is ORTHOGONAL to the per-(variant, dexId) stability dedupe already in
 * `dexLiveScanSession` (which guards against a single bad NUMBER). This gate
 * guards against a single bad VARIANT. A frame must clear BOTH.
 *
 * Pure + framework-agnostic so the gate can be unit-tested deterministically.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** Confidence floor (0-100) for a frame to count toward variant stability. */
export const VARIANT_STABILITY_MIN_CONFIDENCE = 80;
/** Consecutive qualifying same-variant frames required before commit. */
export const VARIANT_STABILITY_FRAMES_REQUIRED = 2;

export interface VariantStabilityFrame {
  variant: PokedexVariant | null;
  /** Frame screen mode, from the adapter. Only "grid_4x4" qualifies. */
  mode: string;
  /** Classifier confidence 0-100. */
  confidence: number;
}

export interface VariantStabilityDecision {
  /** True once the incoming variant has held for the required run length. */
  stable: boolean;
  /** The variant being tracked, or null if this frame did not qualify. */
  variant: PokedexVariant | null;
  /** How many consecutive qualifying frames the current variant has held. */
  runLength: number;
  /**
   * Did THIS frame qualify for the variant gate (grid_4x4, non-null variant,
   * confidence ≥ 80)? The gate only *blocks* a frame that qualifies but has
   * not yet corroborated; a non-qualifying frame (e.g. a low-confidence weak
   * read) is the variant gate's no-op and must fall through to the existing
   * per-number stability logic unchanged. The caller uses this flag to avoid
   * blocking those frames.
   */
  qualified: boolean;
}

/**
 * Create a stateful tracker. Feed each frame in arrival order; it returns
 * whether the frame's variant has cleared the 2-frame gate.
 *
 * Frames that don't qualify (not grid_4x4, confidence < 80, or null variant)
 * do NOT reset the run on their own — they are simply not counted, so a single
 * blurry/low-confidence frame between two good same-variant reads doesn't force
 * the user to start the corroboration over. A qualifying frame whose variant
 * DIFFERS from the current run resets the run to that new variant at length 1.
 */
export function createVariantStabilityTracker() {
  let currentVariant: PokedexVariant | null = null;
  let runLength = 0;

  function observe(frame: VariantStabilityFrame): VariantStabilityDecision {
    const qualifies =
      frame.mode === "grid_4x4" &&
      frame.variant != null &&
      frame.confidence >= VARIANT_STABILITY_MIN_CONFIDENCE;

    if (!qualifies) {
      // Non-qualifying frame: don't advance, don't reset. Report the current
      // tracked variant's stability so a caller polling mid-blur still sees the
      // last good state, but this frame's own variant is "not tracked".
      return {
        stable: runLength >= VARIANT_STABILITY_FRAMES_REQUIRED && currentVariant != null,
        variant: currentVariant,
        runLength,
        qualified: false,
      };
    }

    if (frame.variant === currentVariant) {
      runLength += 1;
    } else {
      currentVariant = frame.variant;
      runLength = 1;
    }

    return {
      stable: runLength >= VARIANT_STABILITY_FRAMES_REQUIRED,
      variant: currentVariant,
      runLength,
      qualified: true,
    };
  }

  return {
    observe,
    /** Current tracked variant (null until a qualifying frame arrives). */
    current(): PokedexVariant | null {
      return currentVariant;
    },
    /** Reset between scan sessions. */
    reset(): void {
      currentVariant = null;
      runLength = 0;
    },
  };
}

export type VariantStabilityTracker = ReturnType<
  typeof createVariantStabilityTracker
>;
